import React, { useEffect, useRef, useState } from "react";
import { ChevronDown, ChevronUp, Eye, Loader2, RotateCcw, Save } from "lucide-react";
import AutomatedEmailPreview from "@/components/settings/AutomatedEmailPreview";
import EmailTemplatePreviewDialog from "@/components/settings/EmailTemplatePreviewDialog";
import EmailVariablesPopover from "@/components/settings/EmailVariablesPopover";
import { Button } from "@/components/ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import HtmlEditor, { type HtmlEditorHandle } from "@/components/ui/html-editor/HtmlEditor";
import { Label } from "@/components/ui/label";
import TrialLocked from "@/components/trial/TrialLocked";
import { cn } from "@/lib/utils";
import type {
  AutomatedEmailTemplate,
  AutomatedEmailTemplateKey,
  AutomatedEmailVariable,
} from "@/services/organizationSettings";

/** Editor and preview cards share one height so they line up, side by side or stacked. */
const PANE_HEIGHT = "h-[32rem] min-[1400px]:h-[36rem]";

interface AutomatedEmailTemplateEditorProps {
  templateKey: AutomatedEmailTemplateKey;
  /** Heading shown above the editor, e.g. "Offer Approved". */
  title: string;
  /** Effective template currently in use (saved override or default). */
  value: AutomatedEmailTemplate;
  defaults: AutomatedEmailTemplate;
  variables: AutomatedEmailVariable[];
  saving: boolean;
  onSave: (template: AutomatedEmailTemplate) => Promise<unknown>;
  /** Persist blanks so the server falls back to the built-in default. */
  onReset: () => Promise<unknown>;
}

const AutomatedEmailTemplateEditor: React.FC<AutomatedEmailTemplateEditorProps> = ({
  templateKey,
  title,
  value,
  defaults,
  variables,
  saving,
  onSave,
  onReset,
}) => {
  const [subject, setSubject] = useState(value.subject);
  const [body, setBody] = useState(value.body);
  const [open, setOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const subjectEditor = useRef<HtmlEditorHandle>(null);
  const bodyEditor = useRef<HtmlEditorHandle>(null);
  // Variables go into whichever field was focused last (the body until the subject is).
  const insertTarget = useRef(bodyEditor);
  const [showPreview, setShowPreview] = useState(true);

  const synced = useRef({ subject: value.subject, body: value.body });

  // Re-sync when the server value changes (save lands, refetch), but never while a
  // request is in flight, and never because one ended: a failed save keeps the edits.
  useEffect(() => {
    const last = synced.current;
    if (saving || (last.subject === value.subject && last.body === value.body)) return;
    synced.current = { subject: value.subject, body: value.body };
    setSubject(value.subject);
    setBody(value.body);
  }, [value.subject, value.body, saving]);

  const dirty = subject !== value.subject || body !== value.body;
  const isDefault = subject === defaults.subject && body === defaults.body;

  const run = async (action: () => Promise<unknown>) => {
    setError(null);
    setSaved(false);
    try {
      await action();
      setSaved(true);
      window.setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not save this email.");
    }
  };

  const idBase = `automated-email-${templateKey}`;

  return (
    <Collapsible
      open={open}
      onOpenChange={setOpen}
      className="space-y-3"
      data-testid={`${idBase}-editor`}
    >
      <CollapsibleTrigger asChild>
        <button
          type="button"
          className="flex w-full flex-wrap items-center justify-between gap-2 rounded-md py-1 text-left hover:bg-muted/50"
          data-testid={`${idBase}-toggle`}
        >
          <span className="flex items-center gap-1.5 text-sm font-medium">
            {open ? (
              <ChevronUp className="h-4 w-4 shrink-0" />
            ) : (
              <ChevronDown className="h-4 w-4 shrink-0" />
            )}
            {title}
          </span>
        </button>
      </CollapsibleTrigger>

      <CollapsibleContent className="space-y-3">
        <TrialLocked lock="automatedEmails">
          <div className="space-y-3">
            <div className="space-y-1.5" onFocus={() => (insertTarget.current = subjectEditor)}>
              <Label
                id={`${idBase}-subject-label`}
                htmlFor={`${idBase}-subject`}
                onClick={() => subjectEditor.current?.focus()}
              >
                Subject
              </Label>
              <HtmlEditor
                ref={subjectEditor}
                singleLine
                id={`${idBase}-subject`}
                aria-labelledby={`${idBase}-subject-label`}
                value={subject}
                onChange={setSubject}
                disabled={saving}
                variables={variables}
                maxLength={255}
              />
            </div>

            <div className="space-y-1.5">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <Label
                  id={`${idBase}-body-label`}
                  htmlFor={`${idBase}-body`}
                  onClick={() => bodyEditor.current?.focus()}
                >
                  Body (HTML)
                </Label>
                <div className="flex items-center gap-2">
                  <EmailVariablesPopover
                    variables={variables}
                    onInsert={(token) => insertTarget.current.current?.insertText(token) ?? false}
                  />
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    aria-pressed={showPreview}
                    onClick={() => setShowPreview((shown) => !shown)}
                    className="aria-pressed:bg-accent aria-pressed:text-accent-foreground"
                  >
                    <Eye className="h-4 w-4" />
                    Preview
                  </Button>
                </div>
              </div>
              {/* Side by side once each column gets ~500px (the settings shell caps the width). */}
              <div
                className={cn("grid gap-4", showPreview && "min-[1400px]:grid-cols-2")}
                onFocus={() => (insertTarget.current = bodyEditor)}
              >
                <HtmlEditor
                  ref={bodyEditor}
                  id={`${idBase}-body`}
                  aria-labelledby={`${idBase}-body-label`}
                  value={body}
                  onChange={setBody}
                  disabled={saving}
                  variables={variables}
                  className={PANE_HEIGHT}
                />
                {showPreview && (
                  <AutomatedEmailPreview
                    templateKey={templateKey}
                    subject={subject}
                    body={body}
                    className={PANE_HEIGHT}
                    action={
                      <EmailTemplatePreviewDialog
                        templateKey={templateKey}
                        title={title}
                        subject={subject}
                        body={body}
                      />
                    }
                  />
                )}
              </div>
            </div>

            {error && <p className="text-xs text-destructive">{error}</p>}

            <div className="flex flex-wrap items-center gap-2">
              <Button
                type="button"
                size="sm"
                onClick={() => void run(() => onSave({ subject, body }))}
                disabled={saving || !dirty}
              >
                {saving ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Save className="h-4 w-4" />
                )}
                {saving ? "Saving..." : "Save email"}
              </Button>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() =>
                  void run(async () => {
                    await onReset();
                    // The effective template may already have been the default.
                    setSubject(defaults.subject);
                    setBody(defaults.body);
                  })
                }
                disabled={saving || (isDefault && !dirty)}
              >
                <RotateCcw className="h-4 w-4" />
                Reset to default
              </Button>
              {saved && <span className="text-xs text-muted-foreground">Saved.</span>}
            </div>
          </div>
        </TrialLocked>
      </CollapsibleContent>
    </Collapsible>
  );
};

export default AutomatedEmailTemplateEditor;
